import React, { ReactNode, useMemo } from 'react'

import { Theme } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import TabSwitch, { TabSwitchOption } from 'components_new/molecules/TabSwitch'

import DataHealthCustomerSelect from 'components_new/organisms/internal/DataHealth/CustomerSelect'
import DataRerunProgressBar from 'components_new/organisms/internal/DataReruns/ProgressBar'

import dayjs from 'utils/dayjs'
import { DataRunsCustomerOption } from 'types/GlobalDataRuns'
import { DataRerunSummary } from 'redux/selectors/internal/DataReruns'
import { DataRunsData } from 'redux/selectors/internal/DataRuns'
import {
  formatDayClock,
  formatRunPeriod,
  isIngestStale
} from 'components_new/organisms/internal/DataRuns/utils'
import { formatRerunProgress } from 'components_new/organisms/internal/DataReruns/utils'

import { DataHealthTab } from './lib'

export interface DataHealthTemplateProps {
  activeTab: DataHealthTab
  /** The newest unfinished rerun, shown as a strip under the run context. */
  activeRerun: DataRerunSummary | null
  /** Shows the Kör om button. */
  canRerun: boolean
  children: ReactNode
  customerOptions: DataRunsCustomerOption[]
  data: DataRunsData | null
  dataFetching: boolean
  /** Total brister in scope, for the Datavalidering tab's pill. */
  flawCount: number | null
  onRefresh: () => void
  onRerun: () => void
  onSelectCustomer: (customerId: string | null) => void
  onSelectTab: (tab: DataHealthTab) => void
  /** null = all customers. */
  selectedCustomerId: string | null
}

/**
 * The shell all Datahälsa tabs sit in: one title, one customer select
 * and one run-context line above a tab bar.
 *
 * The run context is the device that makes the two merged pages feel like one:
 * "Körning 2 augusti 19:55 – 3 augusti 09:46 · hämtad 09:46:09" is the same
 * string on every tab, so a brist and a failed job are visibly about the same
 * night.
 */
const DataHealthTemplate = (props: DataHealthTemplateProps) => {
  const {
    activeTab,
    activeRerun,
    canRerun,
    children,
    customerOptions,
    data,
    dataFetching,
    flawCount,
    onRefresh,
    onRerun,
    onSelectCustomer,
    onSelectTab,
    selectedCustomerId
  } = props

  const runPeriod = useMemo(() => formatRunPeriod(data?.jobs ?? []), [data])

  const failCount = data?.aggregates.fail_count ?? 0

  const selectedCustomerName = selectedCustomerId
    ? customerOptions.find((customer) => customer.id === selectedCustomerId)
      ?.name ?? null
    : null

  const runContext = data
    ? runPeriod
      ? `Körning ${runPeriod} · hämtad ${dayjs(data.fetched_at)
        .tz()
        .format('HH:mm:ss')}`
      : `Hämtad ${dayjs(data.fetched_at).tz().format('HH:mm:ss')}`
    : dataFetching
      ? 'Hämtar…'
      : ''

  /**
   * The counts are rendered into the labels rather than passed as
   * `TabSwitchOption.number`, which colours its pill by selected state only.
   * Here the colour is the point: the run failures are red wherever the
   * cursor happens to be.
   *
   * Datavalidering sits apart on the right: the other tabs are about last
   * night's run, that one is about the data it produced. Its count is plain
   * text, not a pill - it is a size, not an alarm.
   */
  const tabOptions: TabSwitchOption[] = [
    { label: 'Översikt', value: 'overview' },
    {
      label: (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
          Jobb
          {failCount > 0 ? (
            <Chip color="error" label={String(failCount)} size="small" />
          ) : null}
        </Box>
      ),
      value: 'jobs'
    },
    {
      label: (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
          Körningar
          {activeRerun ? (
            <Chip color="info" label="Pågår" size="small" />
          ) : null}
        </Box>
      ),
      value: 'runs'
    },
    {
      label: (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
          <Icon fontSize="small" name="TableRowsOutlined" />
          Datavalidering
          {flawCount ? (
            <Text
              component="span"
              sx={{
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums'
              }}
              variant="body2"
            >
              {flawCount.toLocaleString('sv-SE')}
            </Text>
          ) : null}
        </Box>
      ),
      sx: {
        ml: 'auto',
        pl: '22px',
        fontWeight: 500,
        // 1px x 20px divider at the left edge.
        backgroundImage: ({ palette }: Theme) =>
          `linear-gradient(${palette.divider}, ${palette.divider})`,
        backgroundPosition: 'left center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '1px 20px'
      },
      value: 'validation'
    }
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          flexShrink: 0,
          pt: '14px',
          px: '24px'
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ maxWidth: 680 }}>
            <Text
              component="h1"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.05em',
                lineHeight: 1.1
              }}
              variant="h3"
            >
              Datahälsa
            </Text>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 1.5,
                mt: 0.5
              }}
            >
              <Text
                sx={{
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums'
                }}
                variant="body2"
              >
                {runContext}
              </Text>
              {data && isIngestStale(data.latest_ingested_at) ? (
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                  <Icon
                    fontSize="small"
                    name="WarningAmberOutlined"
                    sx={{ color: 'warning.text' }}
                  />
                  <Text sx={{ color: 'warning.text' }} variant="caption">
                    Data kan vara inaktuell - senaste inläsningen är mer än 26
                    timmar gammal.
                  </Text>
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            {canRerun ? (
              <Button
                onClick={onRerun}
                startIcon={<Icon fontSize="small" name="Rerun" />}
                variant="contained"
              >
                {selectedCustomerName
                  ? `Kör om ${selectedCustomerName}`
                  : 'Kör om alla kunder…'}
              </Button>
            ) : null}
            <Box sx={{ flexShrink: 0, width: 260 }}>
              <DataHealthCustomerSelect
                customers={customerOptions}
                onSelect={onSelectCustomer}
                selectedCustomerId={selectedCustomerId}
              />
            </Box>
            <Button
              startIcon={<Icon fontSize="small" name="IncidentProcess" />}
              to="/internal/data-health/incident-process"
              variant="outlined"
            >
              Incidentprocess
            </Button>
            <Button
              loading={dataFetching}
              onClick={onRefresh}
              startIcon={<Icon fontSize="small" name="RefreshOutlined" />}
              variant="outlined"
            >
              Uppdatera
            </Button>
          </Box>
        </Box>

        {activeRerun ? (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'info.transparent',
              border: '1px solid',
              borderColor: 'info.light',
              borderRadius: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              mt: 1.5,
              p: '4px 8px 4px 12px'
            }}
          >
            <Box
              sx={{
                bgcolor: 'info.main',
                borderRadius: '50%',
                flexShrink: 0,
                height: 8,
                width: 8
              }}
            />
            <Text
              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
              variant="caption"
            >
              Körning pågår
            </Text>
            <Text
              sx={{
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums'
              }}
              variant="caption"
            >
              {`${formatRerunProgress(activeRerun)} · startad ${formatDayClock(
                activeRerun.started_at
              )}${
                activeRerun.trigger_type === 'schedule'
                  ? ' · Schemalagd'
                  : activeRerun.started_by
                    ? ` av ${activeRerun.started_by.name}`
                    : ''
              }`}
            </Text>
            <DataRerunProgressBar
              rerun={activeRerun}
              sx={{ flex: 1, minWidth: 80 }}
            />
            <Button
              onClick={() => onSelectTab('runs')}
              size="small"
              variant="text"
            >
              Visa körningen
            </Button>
          </Box>
        ) : null}

        <TabSwitch
          noGutter
          onChange={(value) => onSelectTab(value as DataHealthTab)}
          options={tabOptions}
          sx={{ mt: '14px' }}
          value={activeTab}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: '24px 24px 48px' }}>
        {children}
      </Box>
    </Box>
  )
}

DataHealthTemplate.displayName = 'DataHealthTemplate'
export default DataHealthTemplate
